import React, { createContext, FC, useContext, useEffect, useState } from "react";

import { registerNetworkErrorInterceptor } from "@/utils/http";

interface OfflineContextType {
  isOffline: boolean;
  lastConnectionCheck: Date | null;
  retryConnection: () => Promise<boolean>;
}

const OfflineContext = createContext<OfflineContextType>({
  isOffline: false,
  lastConnectionCheck: null,
  retryConnection: async () => false,
});

export const useOffline = () => useContext(OfflineContext);

export const OfflineContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOffline, setIsOffline] = useState(false);
  const [lastConnectionCheck, setLastConnectionCheck] = useState<Date | null>(null);

  // Check if the user is offline using navigator.onLine
  const checkOnlineStatus = () => {
    const onlineStatus = navigator.onLine;
    setIsOffline(!onlineStatus);
    setLastConnectionCheck(new Date());
    return onlineStatus;
  };

  // Enhanced connectivity check by attempting to reach a known endpoint
  const testNetworkConnectivity = async (): Promise<boolean> => {
    try {
      // Try to fetch a small resource to test actual connectivity
      const response = await fetch(window.location.origin + '/favicon.ico', {
        cache: 'no-cache',
        method: 'HEAD',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });
      return response.ok;
    } catch (error) {
      console.warn('Network connectivity test failed:', error);
      return false;
    }
  };

  // Combined check using both navigator.onLine and actual network test
  const performFullConnectivityCheck = async (): Promise<boolean> => {
    const navigatorOnline = navigator.onLine;
    
    if (!navigatorOnline) {
      setIsOffline(true);
      setLastConnectionCheck(new Date());
      return false;
    }

    // If navigator says we're online, double-check with network test
    const actuallyOnline = await testNetworkConnectivity();
    setIsOffline(!actuallyOnline);
    setLastConnectionCheck(new Date());
    return actuallyOnline;
  };

  // Retry connection function
  const retryConnection = async (): Promise<boolean> => {
    const isConnected = await performFullConnectivityCheck();
    return isConnected;
  };

  useEffect(() => {
    // Initial check
    performFullConnectivityCheck();

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('Network: Back online');
      performFullConnectivityCheck();
    };

    const handleOffline = () => {
      console.log('Network: Gone offline');
      setIsOffline(true);
      setLastConnectionCheck(new Date());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for network errors from HTTP requests
    registerNetworkErrorInterceptor((isNetworkError) => {
      if (isNetworkError) {
        setIsOffline(true);
        setLastConnectionCheck(new Date());
      }
    });

    // Periodic connectivity check (every 30 seconds)
    const interval = setInterval(() => {
      if (isOffline) {
        performFullConnectivityCheck();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [isOffline]);

  const value = {
    isOffline,
    lastConnectionCheck,
    retryConnection,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};
